import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, Users, RefreshCw, Building2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    gst_number: "",
    address: ""
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/vendors`, formData, { headers });
      }

      closeModal();
      fetchVendors();
    } catch (err) {
      console.error("Failed to save vendor:", err);
      alert("Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteVendor = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vendors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVendors();
    } catch (err) {
      console.error("Failed to delete vendor:", err);
      alert("Failed to delete vendor");
    }
  };

  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        contact_person: vendor.contact_person,
        email: vendor.email || "",
        phone: vendor.phone,
        gst_number: vendor.gst_number || "",
        address: vendor.address
      });
    } else {
      setEditingVendor(null);
      setFormData({
        name: "",
        contact_person: "",
        email: "",
        phone: "",
        gst_number: "",
        address: ""
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setFormData({
      name: "",
      contact_person: "",
      email: "",
      phone: "",
      gst_number: "",
      address: ""
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-primary" data-testid="vendors-title">Vendors</h1>
          <p className="text-muted-foreground">Manage your supplier information</p>
        </div>
        <Button onClick={() => openModal()} data-testid="add-vendor-button" className="min-h-[44px]">
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{vendors.length}</div>
              <div className="text-sm text-muted-foreground">Total Vendors</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Grid */}
      {vendors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground mb-4" data-testid="no-vendors-message">No vendors yet</p>
            <Button onClick={() => openModal()} className="min-h-[44px]">
              Add Your First Vendor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendors-grid">
          {vendors.map((vendor) => (
            <Card key={vendor.id} data-testid={`vendor-card-${vendor.id}`} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{vendor.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal(vendor)}
                      data-testid={`edit-vendor-${vendor.id}`}
                      className="min-h-[44px] min-w-[44px] p-2"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteVendor(vendor.id, vendor.name)}
                      data-testid={`delete-vendor-${vendor.id}`}
                      className="min-h-[44px] min-w-[44px] p-2 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Contact Person</p>
                  <p className="font-medium">{vendor.contact_person}</p>
                </div>
                {vendor.email && (
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="truncate">{vendor.email}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p>{vendor.phone}</p>
                </div>
                {vendor.gst_number && (
                  <div>
                    <p className="text-xs text-muted-foreground">GST Number</p>
                    <p className="font-mono text-sm">{vendor.gst_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Vendor Address</p>
                  <p className="text-xs line-clamp-2">{vendor.address}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal(); else setShowModal(true); }}>
        <DialogContent className="max-w-2xl" data-testid="vendor-modal">
          <DialogHeader>
            <DialogTitle>{editingVendor ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
            <DialogDescription>
              {editingVendor ? "Update vendor information" : "Add a new vendor to your system"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} data-testid="vendor-form" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="vendor-name-input"
                  required
                  placeholder="Enter vendor name"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person *</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  data-testid="contact-person-input"
                  required
                  placeholder="Enter contact person"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="vendor-email-input"
                  placeholder="Enter email (optional)"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="vendor-phone-input"
                  required
                  placeholder="Enter phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>GST Number (Optional)</Label>
                <Input
                  value={formData.gst_number}
                  onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                  data-testid="vendor-gst-input"
                  placeholder="Enter GST number (optional)"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Vendor Address *</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="vendor-address-input"
                  required
                  rows={3}
                  placeholder="Enter vendor address"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal} data-testid="cancel-vendor-button" className="min-h-[44px]">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-vendor-button" className="min-h-[44px]">
                {submitting ? 'Saving...' : (editingVendor ? "Update" : "Add")} Vendor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
